/**
 * Pipeline Keyboard Shortcuts Composable
 * UX/UI Sprint 2 - Task 2.4
 *
 * Provides keyboard navigation and shortcuts for the Pipeline board
 * Shortcuts:
 * - n: New card
 * - /: Focus search
 * - ?: Show keyboard shortcuts modal
 * - Escape: Close modal/menu
 * - Arrow keys: Navigate between cards/columns
 */

import { ref, onActivated, onDeactivated, onMounted, onUnmounted } from 'vue';

/**
 * Keyboard shortcuts for Pipeline
 * @param {Object} options - Configuration options
 * @param {Function} options.onNewCard - Callback when 'n' is pressed
 * @param {Function} options.onSearch - Callback when '/' is pressed
 * @param {Function} options.onShowShortcuts - Callback when '?' is pressed
 * @param {Function} options.onEscape - Callback when 'Escape' is pressed
 * @param {Function} options.onSave - Callback when 'Ctrl+Enter' is pressed
 */
export const usePipelineKeyboard = (options = {}) => {
  const isEnabled = ref(true);
  let isListening = false;

  // Check if we're in an input/textarea context
  const isInputContext = () => {
    const el = document.activeElement;
    if (!el) return false;

    const tagName = el.tagName.toLowerCase();
    const isInput =
      tagName === 'input' || tagName === 'textarea' || tagName === 'select';
    const isEditable = el.isContentEditable;

    return isInput || isEditable;
  };

  // Handle keyboard events
  const handleKeydown = event => {
    if (!isEnabled.value) return;

    const { key, ctrlKey, metaKey, shiftKey } = event;
    const inInput = isInputContext();

    // Ctrl/Cmd + Enter: Save (works even in inputs)
    if ((ctrlKey || metaKey) && key === 'Enter') {
      if (options.onSave) {
        event.preventDefault();
        options.onSave();
      }
      return;
    }

    // Escape: Always works
    if (key === 'Escape') {
      if (options.onEscape) {
        options.onEscape();
      }
      return;
    }

    // Don't trigger single-key shortcuts when typing
    if (inInput) return;

    // Single key shortcuts (only when not in input)
    switch (key.toLowerCase()) {
      case 'n':
        // New card
        event.preventDefault();
        if (options.onNewCard) {
          options.onNewCard();
        }
        break;

      case '/':
        // Focus search
        event.preventDefault();
        if (options.onSearch) {
          options.onSearch();
        }
        break;

      case '?':
        // Show shortcuts modal (Shift + /)
        if (shiftKey) {
          event.preventDefault();
          if (options.onShowShortcuts) {
            options.onShowShortcuts();
          }
        }
        break;

      default:
        break;
    }
  };

  // Enable/disable shortcuts
  const enable = () => {
    isEnabled.value = true;
  };

  const disable = () => {
    isEnabled.value = false;
  };

  const startListening = () => {
    if (isListening) return;
    document.addEventListener('keydown', handleKeydown);
    isListening = true;
  };

  const stopListening = () => {
    if (!isListening) return;
    document.removeEventListener('keydown', handleKeydown);
    isListening = false;
  };

  // KeepAlive preserves mounted component instances. Activation hooks ensure
  // only the visible pipeline view owns the global shortcuts.
  onMounted(startListening);
  onActivated(startListening);
  onDeactivated(stopListening);
  onUnmounted(stopListening);

  return {
    isEnabled,
    enable,
    disable,
  };
};

/**
 * Keyboard shortcuts definition for display in modal
 */
export const PIPELINE_SHORTCUTS = [
  {
    category: 'GENERAL',
    items: [
      { keys: ['n'], description: 'NEW_CARD' },
      { keys: ['/', 'Ctrl', 'K'], description: 'SEARCH' },
      { keys: ['?'], description: 'SHOW_SHORTCUTS' },
      { keys: ['Esc'], description: 'CLOSE_MODAL' },
    ],
  },
  {
    category: 'NAVIGATION',
    items: [
      { keys: ['←', '→'], description: 'NAVIGATE_COLUMNS' },
      { keys: ['↑', '↓'], description: 'NAVIGATE_CARDS' },
      { keys: ['Enter'], description: 'OPEN_CARD' },
      { keys: ['Tab'], description: 'NEXT_ELEMENT' },
    ],
  },
  {
    category: 'ACTIONS',
    items: [
      { keys: ['Ctrl', 'Enter'], description: 'SAVE_AND_CLOSE' },
      { keys: ['Space'], description: 'VIEW_DETAILS' },
    ],
  },
];

export default usePipelineKeyboard;
